import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { tap } from 'rxjs';

import { InputMaskDirective } from '../../../sdk/forms/mask';

interface GroupedTimezones {
  continent: string;
  timezones: string[];
}
@Component({
  selector: 'rms-timezone-selector',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatAutocompleteModule,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    InputMaskDirective,
  ],
  templateUrl: './timezone-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimezoneSelectorComponent),
      multi: true,
    },
  ],
})
export class TimezoneSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() public placeholder?: string | null = null;
  @Input() public selectorHint: string | null = null;

  private readonly destroy$ = inject(DestroyRef);

  protected searchControl = new FormControl<string>('');

  protected filteredGroupedTimezones: GroupedTimezones[] = [];
  private groupedTimezones: GroupedTimezones[] = [];
  private timezones = Intl.supportedValuesOf('timeZone');

  private onChange?: (value: string) => void;
  private onTouched?: () => void;

  ngOnInit(): void {
    this.groupedTimezones = this.groupTimezonesByContinent();
    this.filteredGroupedTimezones = [...this.groupedTimezones];

    this.searchControl.valueChanges
      .pipe(
        tap((value) => {
          this.filterTimezones(value);
          this.onTouched?.();
          this.onChange?.(value ?? '');
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  public mask = (text: string): string =>
    text
      .trim()
      .replace(/[^a-zA-Z/]/g, '')
      .replace(/(?:^|\/)([a-z])/g, (_, letter) => '/' + letter.toUpperCase())
      .replace(/^\//, '');

  writeValue(value: string): void {
    value && this.searchControl.setValue(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.searchControl.disable() : this.searchControl.enable();
  }

  protected onSelectionChange(timezone: string): void {
    this.searchControl.setValue(timezone);
  }

  private groupTimezonesByContinent(): GroupedTimezones[] {
    const timezones = [...this.timezones];

    const continents: Record<string, string[]> = {};
    timezones.forEach((tz) => {
      const [continent, _] = tz.split('/');
      if (!continents[continent]) continents[continent] = [];
      continents[continent].push(tz);
    });

    return Object.entries(continents).map(([continent, timezones]) => ({
      continent,
      timezones,
    }));
  }

  private filterTimezones(value: string | null): void {
    const search = value;

    if (!search || !search.length || this.timezones.includes(search)) {
      this.filteredGroupedTimezones = [...this.groupedTimezones];
    } else {
      this.filteredGroupedTimezones = this.groupedTimezones
        .map((group) => ({
          continent: group.continent,
          timezones: group.timezones.filter((tz) => tz.toLowerCase().includes(search.toLowerCase())),
        }))
        .filter((group) => group.timezones.length > 0);
    }
  }
}
